import { ApiClientError, NetworkError } from '@recipes/api-client';
import type { CookingLogWriteInput, Recipe, VersionConflictDetails } from '@recipes/contracts';
import { api } from '@/lib/api';
import { offlineDb, type PendingAction, type SyncConflict } from './db';

export interface CookingNotePayload {
  note: string;
  /** Recipe version the note was written against; a newer server version yields a conflict. */
  baseVersion: number;
}
export interface FavoritePayload {
  favorite: boolean;
}
export interface RatingPayload {
  stars: number | null;
}
export type CookingLogPayload = CookingLogWriteInput;

export type PendingPayloadByKind = {
  'cooking-note': CookingNotePayload;
  favorite: FavoritePayload;
  rating: RatingPayload;
  'cooking-log': CookingLogPayload;
  'step-check': { stepId: string; completed: boolean };
  'shopping-check': unknown;
};

export interface ReplayResult {
  done: number;
  failed: number;
  conflicts: number;
  /** True when the loop stopped because the network was unreachable. */
  offline: boolean;
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());
let replaying: Promise<ReplayResult> | null = null;
let started = false;

/** Append a note to the recipe's notes field (server-side text, newline separated). */
export function appendNote(existing: string | null, note: string): string {
  const stamp = new Date().toISOString().slice(0, 10);
  const line = `[${stamp}] ${note.trim()}`;
  return existing && existing.trim() ? `${existing.trimEnd()}\n${line}` : line;
}

export async function enqueuePendingAction<K extends keyof PendingPayloadByKind>(
  kind: K,
  recipeId: string | null,
  payload: PendingPayloadByKind[K],
): Promise<number> {
  const id = await offlineDb.pendingActions.add({
    kind,
    recipeId,
    payload,
    createdAt: Date.now(),
    attempts: 0,
  });
  notify();
  return id as number;
}

export function subscribePendingQueue(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

async function recordConflict(action: PendingAction, error: ApiClientError): Promise<void> {
  const details = error.error.details as VersionConflictDetails | undefined;
  const conflict: SyncConflict = {
    kind: action.kind,
    recipeId: action.recipeId ?? '',
    localPayload: action.payload,
    serverRecipe: (details?.current as Recipe | undefined) ?? null,
    message: error.message,
    createdAt: Date.now(),
  };
  await offlineDb.syncConflicts.add(conflict);
}

/** Execute one queued action against the API. Throws on failure. */
async function runAction(action: PendingAction): Promise<'done' | 'skipped'> {
  const recipeId = action.recipeId;
  switch (action.kind) {
    case 'cooking-note': {
      if (!recipeId) return 'done';
      const payload = action.payload as CookingNotePayload;
      // Read the notes as they are on the server, but submit with the version the note was
      // written against, so a concurrent edit surfaces as a 409 (spec 15: no silent merge).
      const current = await api.recipes.get(recipeId);
      await api.recipes.update(recipeId, {
        version: payload.baseVersion,
        notes: appendNote(current.notes, payload.note),
        revisionReason: 'Cooking note',
      });
      return 'done';
    }
    case 'cooking-log': {
      if (!recipeId) return 'done';
      await api.ratings.addCookingLog(recipeId, action.payload as CookingLogPayload);
      return 'done';
    }
    case 'favorite': {
      if (!recipeId) return 'done';
      await api.recipes.setFavorite(recipeId, (action.payload as FavoritePayload).favorite);
      return 'done';
    }
    case 'rating': {
      if (!recipeId) return 'done';
      await api.ratings.set(recipeId, { stars: (action.payload as RatingPayload).stars });
      return 'done';
    }
    case 'step-check':
      // Step checks live only in the local cooking session; nothing to sync.
      return 'done';
    default:
      // Other feature areas own their own kinds (e.g. shopping-check); leave them alone.
      return 'skipped';
  }
}

/**
 * Replay queued actions in order. Stops at the first network failure; records version
 * conflicts for the user to resolve; drops actions that failed permanently 3 times.
 */
export function replayPendingActions(): Promise<ReplayResult> {
  if (replaying) return replaying;
  replaying = (async () => {
    const result: ReplayResult = { done: 0, failed: 0, conflicts: 0, offline: false };
    const actions = await offlineDb.pendingActions.orderBy('createdAt').toArray();
    for (const action of actions) {
      try {
        const outcome = await runAction(action);
        if (outcome === 'done') {
          await offlineDb.pendingActions.delete(action.id!);
          result.done += 1;
        }
      } catch (error) {
        if (error instanceof NetworkError) {
          result.offline = true;
          break;
        }
        // Not signed in (yet): right after reconnecting the token refresh may still be in
        // flight or was aborted by a navigation. That says nothing about the action itself –
        // keep the queue untouched and try again on the next trigger instead of dropping it.
        if (error instanceof ApiClientError && error.status === 401) {
          result.failed += 1;
          break;
        }
        if (error instanceof ApiClientError && error.code === 'VERSION_CONFLICT') {
          await recordConflict(action, error);
          await offlineDb.pendingActions.delete(action.id!);
          result.conflicts += 1;
          continue;
        }
        const attempts = action.attempts + 1;
        const message = error instanceof Error ? error.message : String(error);
        const permanent =
          attempts >= 3 ||
          (error instanceof ApiClientError && error.status < 500 && error.status !== 429);
        if (permanent) await offlineDb.pendingActions.delete(action.id!);
        else await offlineDb.pendingActions.update(action.id!, { attempts, lastError: message });
        result.failed += 1;
      }
    }
    notify();
    return result;
  })().finally(() => {
    replaying = null;
  });
  return replaying;
}

/** Resolve a conflict by appending the local note on top of the server version. */
export async function resolveConflictKeepBoth(conflict: SyncConflict): Promise<void> {
  if (conflict.kind === 'cooking-note') {
    const payload = conflict.localPayload as CookingNotePayload;
    const current = await api.recipes.get(conflict.recipeId);
    await api.recipes.update(conflict.recipeId, {
      version: current.version,
      notes: appendNote(current.notes, payload.note),
      revisionReason: 'Cooking note (merged after conflict)',
    });
  }
  await offlineDb.syncConflicts.delete(conflict.id!);
  notify();
}

export async function discardConflict(conflict: SyncConflict): Promise<void> {
  await offlineDb.syncConflicts.delete(conflict.id!);
  notify();
}

/** Idempotent: listens for connectivity and replays the queue. Called from any screen. */
export function startSyncManager(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('online', () => {
    void replayPendingActions();
  });
  if (navigator.onLine) void replayPendingActions();
}
